import { useMemo } from "react";
import type {
  MediaMetadata,
  PlatformRequirements,
} from "../components/brandDetails/tabs/creator-studio/platforms/types";

const REQUIREMENTS: PlatformRequirements = {
  maxImages: 20,
  maxVideos: 1,
  imageFormats: "JPEG, PNG, GIF",
  videoFormats: "MP4, MOV, AVI",
  maxImageSize: "5 MB per image",
  maxVideoSize: "5 GB",
  videoDuration: "10 min",
  recommended: "1200 × 627 px (1.91:1)",
  minDimensions: "552 × 276 px",
  maxDimensions: "8192 × 8192 px",
  aspectRatios: "1.91:1 (landscape), 1:1 (square), 1:1.25 (portrait)",
  note: "Supports PDF documents (up to 100 MB, 300 pages)",
  minWidth: 552,
  minHeight: 276,
  maxWidth: 8192,
  maxHeight: 8192,
};

export interface LinkedInFormValidation {
  imageCountValid: boolean | null;
  imageSizeValid: boolean | null;
  dimensionsValid: boolean | null;
  aspectRatiosValid: boolean | null;
  videoCountValid: boolean | null;
  videoDurationValid: boolean | null;
}

export function useLinkedInForm(mediaMetadata: MediaMetadata[]) {
  const maxImages =
    typeof REQUIREMENTS.maxImages === "number" ? REQUIREMENTS.maxImages : 10;

  const validation = useMemo((): LinkedInFormValidation => {
    const enabledMedia = mediaMetadata.slice(0, maxImages);

    if (enabledMedia.length === 0) {
      return {
        imageCountValid: null,
        imageSizeValid: null,
        dimensionsValid: null,
        aspectRatiosValid: null,
        videoCountValid: null,
        videoDurationValid: null,
      };
    }

    const images = enabledMedia.filter((m) => m.type === "image");
    const videos = enabledMedia.filter((m) => m.type === "video");

    const imageCountValid =
      images.length === 0 ? null : images.length <= maxImages;

    const maxImageSizeBytes = 5 * 1024 * 1024;
    const imageSizeValid =
      images.length === 0
        ? null
        : images.every((img) => img.size <= maxImageSizeBytes);

    const dimensionsValid =
      images.length === 0
        ? null
        : images.every(
            (img) =>
              img.width >= (REQUIREMENTS.minWidth ?? 0) &&
              img.height >= (REQUIREMENTS.minHeight ?? 0) &&
              img.width <= (REQUIREMENTS.maxWidth ?? Infinity) &&
              img.height <= (REQUIREMENTS.maxHeight ?? Infinity)
          );

    const maxVideos =
      typeof REQUIREMENTS.maxVideos === "number"
        ? REQUIREMENTS.maxVideos
        : Infinity;
    const videoCountValid =
      videos.length === 0 ? null : videos.length <= maxVideos;

    const videoDurationValid =
      videos.length === 0
        ? null
        : videos.every((video) => (video.duration ?? 0) <= 600);

    return {
      imageCountValid,
      imageSizeValid,
      dimensionsValid,
      aspectRatiosValid: null,
      videoCountValid,
      videoDurationValid,
    };
  }, [mediaMetadata, maxImages]);

  return { validation, requirements: REQUIREMENTS };
}
